<script>
import { mapActions, mapGetters } from 'vuex';
import RbxRemoteCombobox from '@/shared/reybex/RbxRemoteCombobox';
import RbxRemoteComboboxMultiple from '@/shared/reybex/RbxRemoteComboboxMultiple';
import RbxTextEditor from '@/shared/reybex/RbxTextEditor';
import fieldVisibilityMixin from '@/mixins/fieldVisibility';
import validatorMixin from '@/mixins/validator';
import AffiliateContactPersonDialog from './AffiliateContactPersonDialog';

export default {
  name: 'AffiliateBasicInfoForm',
  components: {
    RbxRemoteCombobox,
    RbxRemoteComboboxMultiple,
    RbxTextEditor,
    AffiliateContactPersonDialog,
  },
  mixins: [fieldVisibilityMixin, validatorMixin],
  props: {
    affiliate: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      states: ['', 'Baden-Württemberg', 'Bayern', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg', 'Hessen',
        'Mecklenburg-Vorpommern', 'Niedersachsen', 'Nordrhein-Westfalen', 'Rheinland-Pfalz', 'Saarland',
        'Sachsen', 'Sachsen-Anhalt', 'Schleswig-Holstein', 'Thüringen'],
      contactPersonDialog: {
        contactPerson: {},
        visible: false,
        isCreateMode: false,
      },
      rules: {
        firm: { required: true },
      },
      hiddenFieldsDomain: 'affiliate',
    };
  },
  computed: {
    sortedContactPersons() {
      const c = this.affiliate.contactPersons ? _.orderBy(this.affiliate.contactPersons, 'id') : []; // eslint-disable-line

      return c;
    },
    ...mapGetters('app', ['auth']),
  },
  methods: {
    ...mapActions('affiliateDetails', ['deleteAffiliateContactPerson']),
    openAffiliateContactPersonDialog(contactPerson, isCreateMode = false) {
      if (isCreateMode) {
        // This is required for saving, without affiliate.id it is not possible
        this.contactPersonDialog.contactPerson = {
          affiliate: {
            id: this.affiliate.id,
          },
        };
      } else {
        this.contactPersonDialog.contactPerson = contactPerson;
      }

      this.contactPersonDialog.visible = true;
      this.contactPersonDialog.isCreateMode = isCreateMode;
    },
    closeAffiliateContactPersonDialog() {
      this.contactPersonDialog.visible = false;
    },
    removeContactPerson(index, contactPerson) {
      this.affiliate.contactPersons.splice(index, 1);

      return this.deleteAffiliateContactPerson(contactPerson);
    },
    submitAffiliateContactPersonDialog({ formData, isCreateMode }) {
      const list = this.affiliate.contactPersons.slice();
      if (isCreateMode) {
        list.push(formData);
      } else {
        const index = list.findIndex(s => s.id === formData.id);
        list[index] = formData;
      }

      this.affiliate.contactPersons = list;
      this.$emit('save'); // Call affiliate detail save
    },
  },
};
</script>

<template>
  <div class="affiliate-detail-tab main-tab">
    <v-layout row wrap>
      <!-- Basic information left column -->
      <v-flex md8 xs12>
        <h4 class="affiliate-detail-tab-title">Allgemein</h4>
        <v-layout row wrap>

          <v-flex v-if="isFieldVisible('affiliate.affiliateType')" md4 py-0>
            <rbx-remote-combobox
              v-model="affiliate.affiliateType"
              label="Art"
              :selected="affiliate.affiliateType"
              domain="affiliateType"
            />
          </v-flex>

          <v-flex v-if="isFieldVisible('affiliate.firm')" md4 py-0>
            <v-text-field
              v-model="affiliate.firm"
              :error-messages="validationErrorMessages.firm"
              label="Firma"
              height="22"
              class="caption"
            />
          </v-flex>

          <v-flex md4>
            <v-layout row wrap>
              <v-flex md6 py-0>
                <v-switch
                  v-model="affiliate.isActive"
                  label="Aktiv"
                  height="0"
                />
              </v-flex>
              <v-flex md6 py-0>
                <v-switch :readonly="!auth.hasDeleteRights"
                          v-model="affiliate.isDelete"
                          label="Gelöscht"
                          height="0"
                />
              </v-flex>
            </v-layout>
          </v-flex>

          <v-flex v-if="isFieldVisible('affiliate.salutation')" md4 py-0>
            <rbx-remote-combobox
              v-model="affiliate.salutation"
              label="Anrede"
              :selected="affiliate.salutation"
              domain="salutation"
            />
          </v-flex>

          <v-flex v-if="isFieldVisible('affiliate.firstname')" md4 py-0>
            <v-text-field
              v-model="affiliate.firstname"
              label="Vorname"
              height="22"
              class="caption"
            />
          </v-flex>

          <v-flex v-if="isFieldVisible('affiliate.surname')" md4 py-0>
            <v-text-field
              v-model="affiliate.surname"
              label="Nachname"
              height="22"
              class="caption"
            />
          </v-flex>

          <v-flex xs12 md12 v-if="isFieldVisible('affiliate.comment')">
            <rbx-text-editor
              label="Kommentar"
              v-model="affiliate.comment"
            />
          </v-flex>

        </v-layout>

        <h4 class="affiliate-detail-tab-title">Adressdaten</h4>
        <v-layout row wrap>

          <v-flex v-if="isFieldVisible('affiliate.street')" md4 py-0>
            <v-text-field
              v-model="affiliate.street"
              label="Straße"
              height="22"
              class="caption"
            />
          </v-flex>

          <v-flex v-if="isFieldVisible('affiliate.zip')" md4 py-0>
            <v-text-field
              v-model="affiliate.zip"
              label="PLZ"
              height="22"
              class="caption"
            />
          </v-flex>

          <v-flex v-if="isFieldVisible('affiliate.location')" md4 py-0>
            <v-text-field
              v-model="affiliate.location"
              label="Ort"
              height="22"
              class="caption"
            />
          </v-flex>

          <v-flex v-if="isFieldVisible('affiliate.country')" md4 py-0>
            <v-text-field
              v-model="affiliate.country"
              label="Land"
              height="22"
              class="caption"
            />
          </v-flex>

          <v-flex v-if="isFieldVisible('affiliate.state')" md4 py-0>
            <v-combobox
              v-model="affiliate.state"
              :items="states"
              label="Bundesland"
              height="22"
              domain="affiliateType"
              class="caption"
            />
          </v-flex>

        </v-layout>

        <h4 class="affiliate-detail-tab-title">Kontaktdaten</h4>
        <v-layout row wrap>

          <v-flex v-if="isFieldVisible('affiliate.phone')" md4 py-0>
            <v-text-field
              v-model="affiliate.phone"
              label="Telefon"
              height="22"
              class="caption"
            />
          </v-flex>

          <v-flex v-if="isFieldVisible('affiliate.phone2')" md4 py-0>
            <v-text-field
              v-model="affiliate.phone2"
              label="Telefon 2"
              height="22"
              class="caption"
            />
          </v-flex>

          <v-flex v-if="isFieldVisible('affiliate.mobile')" md4 py-0>
            <v-text-field
              v-model="affiliate.mobile"
              label="Handy"
              height="22"
              class="caption"
            />
          </v-flex>

          <v-flex v-if="isFieldVisible('affiliate.fax')" md4 py-0>
            <v-text-field
              v-model="affiliate.fax"
              label="Fax"
              height="22"
              class="caption"
            />
          </v-flex>

          <v-flex v-if="isFieldVisible('affiliate.email')" md4 py-0>
            <v-text-field
              v-model="affiliate.email"
              label="E-Mail"
              height="22"
              class="caption"
            />
          </v-flex>

          <v-flex v-if="isFieldVisible('affiliate.web')" md4 py-0>
            <v-text-field
              v-model="affiliate.web"
              label="Webseite"
              height="22"
              class="caption"
            />
          </v-flex>

        </v-layout>
      </v-flex>

      <v-flex md4 xs12>
        <v-layout row wrap>

          <v-flex xs12 py-0>
            <div class="affiliate-detail-common-list">
              <div class="affiliate-detail-common-list-title">
                <b>Ansprechpartner</b>
                <v-tooltip bottom>
                  <v-btn
                    @click="openAffiliateContactPersonDialog(null, true)"
                    slot="activator"
                    small fab flat
                  >
                    <v-icon>add</v-icon>
                  </v-btn>
                  <span>Neuer Ansprechpartner</span>
                </v-tooltip>
              </div>

              <small
                v-if="affiliate.contactPersons.length === 0">
                Keine Ansprechpartner vorhanden.
              </small>

              <div
                v-else
                v-for="(contactPerson, index) in sortedContactPersons"
                :key="contactPerson.id"
                class="affiliate-detail-common-list-item"
              >
                <a @click="openAffiliateContactPersonDialog(contactPerson)" :key="contactPerson.id">
                  {{ contactPerson.firstname }} {{ contactPerson.surname }}
                  <v-icon v-if="contactPerson.isActive" small>check</v-icon>
                </a>
                <v-btn v-if="auth.hasDeleteRights"
                       @click="removeContactPerson(index, contactPerson)" fab small flat>
                  <v-icon>delete</v-icon>
                </v-btn>
              </div>

            </div>
          </v-flex>

        </v-layout>
      </v-flex>
    </v-layout>

    <AffiliateContactPersonDialog
      v-if="contactPersonDialog.visible"
      :contact-person="contactPersonDialog.contactPerson"
      :is-create-mode="contactPersonDialog.isCreateMode"
      @cancel="closeAffiliateContactPersonDialog"
      @submit="submitAffiliateContactPersonDialog"
    />
  </div>
</template>
